import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Reveal } from "./Motion";
import { SERVICES } from "../lib/site";

export default function Services() {
    const [active, setActive] = useState(0);

    return (
        <section id="services" data-testid="services-section" className="px-5 md:px-10 py-24 md:py-32">
            <Reveal>
                <div className="mb-16 md:mb-24 flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
                    <div>
                        <p className="eyebrow mb-6 flex items-center gap-3">
                            <span className="h-px w-10 bg-vermilion" /> What we do
                        </p>
                        <h2 className="display-xl font-display font-light text-paper">
                            Six disciplines.
                            <br />
                            One obsession<span className="text-vermilion">:</span> attention.
                        </h2>
                    </div>
                    <p className="max-w-sm text-sm md:text-base font-light leading-relaxed text-smoke">
                        Bought, built, posted and pitched — every lever of fame, run by one
                        senior team and measured by one scoreboard.
                    </p>
                </div>
            </Reveal>

            <div className="grid gap-12 lg:grid-cols-2 lg:gap-20 items-start">
                <div data-testid="services-list">
                    {SERVICES.map((s, i) => (
                        <Reveal key={s.num} delay={i * 0.06}>
                            <button
                                data-testid={`service-row-${i + 1}`}
                                onMouseEnter={() => setActive(i)}
                                onFocus={() => setActive(i)}
                                onClick={() => setActive(i)}
                                className={`group w-full border-t border-line py-8 md:py-10 text-left transition-colors duration-500 ${
                                    active === i ? "bg-card/40" : ""
                                } last:border-b`}
                            >
                                <div className="flex items-start justify-between gap-6 px-2 md:px-4">
                                    <div className="flex items-baseline gap-5 md:gap-8">
                                        <span
                                            className={`font-mono text-xs transition-colors duration-300 ${
                                                active === i ? "text-vermilion" : "text-smoke"
                                            }`}
                                        >
                                            {s.num}
                                        </span>
                                        <h3
                                            className={`font-display text-3xl md:text-5xl font-light transition-colors duration-300 ${
                                                active === i ? "text-vermilion" : "text-paper"
                                            }`}
                                        >
                                            {s.title}
                                        </h3>
                                    </div>
                                    <ArrowUpRight
                                        className={`mt-2 h-6 w-6 shrink-0 transition-all duration-500 ${
                                            active === i
                                                ? "text-vermilion opacity-100 translate-x-0"
                                                : "text-smoke opacity-30 -translate-x-2"
                                        }`}
                                    />
                                </div>
                                <p className="mt-4 px-2 md:px-4 max-w-xl text-sm font-light leading-relaxed text-smoke">
                                    {s.desc}
                                </p>
                            </button>
                        </Reveal>
                    ))}
                </div>

                <div className="sticky top-28 hidden lg:block">
                    <div className="relative aspect-[4/5] w-full overflow-hidden border border-line bg-card">
                        <AnimatePresence mode="popLayout">
                            <motion.img
                                key={active}
                                src={SERVICES[active].image}
                                alt={SERVICES[active].title}
                                initial={{ opacity: 0, scale: 1.06 }}
                                animate={{ opacity: 1, scale: 1 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                                className="absolute inset-0 h-full w-full object-cover"
                            />
                        </AnimatePresence>
                        <div className="absolute inset-0 bg-gradient-to-t from-ink/70 via-transparent to-transparent" />
                        <div className="absolute bottom-5 left-5 flex items-center gap-3">
                            <span className="font-mono text-[10px] tracking-[0.25em] text-paper/90">
                                {SERVICES[active].num} — {SERVICES[active].title}
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
